import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button, Divider } from "@heroui/react";
import { FiLogOut, FiLock, FiRefreshCcw, FiX } from "react-icons/fi";
import ConfirmationModal from "@/components/modal/ConfirmationModal";
import { getCurrencyFlag as getCurrencyFlagUtil } from "@/utils/currencies";

interface LearningDay {
  id: number;
  title: string;
  isExpanded: boolean;
  isLocked: boolean;
  activities: {
    id: number;
    title: string;
    isLocked: boolean;
    isActive?: boolean;
  }[];
}

interface LearningAreaSidebarProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
  activeLesson?: string;
  setActiveLesson?: (lesson: string) => void;
}

const LearningAreaSidebar: React.FC<LearningAreaSidebarProps> = ({
  isSidebarOpen,
  setIsSidebarOpen,
  activeLesson,
  setActiveLesson,
}) => {
  const { currentUser, logout } = useAuth();
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [isResetOpen, setIsResetOpen] = useState(false);

  const [learningDays, setLearningDays] = useState<LearningDay[]>([
    {
      id: 1,
      title: "Day 1",
      isExpanded: true,
      isLocked: false,
      activities: [
        {
          id: 1,
          title: "The Accounting Equation",
          isLocked: false,
          isActive: true,
        },
        {
          id: 2,
          title: "Current and Non-Current Assets and Liabilities",
          isLocked: false,
        },
        {
          id: 3,
          title: "Debits and Credits",
          isLocked: false,
        },
      ],
    },
    {
      id: 2,
      title: "Day 2",
      isExpanded: false,
      isLocked: true,
      activities: [
        {
          id: 4,
          title: "Certificate",
          isLocked: true,
        },
      ],
    },
  ]);

  const currencyFlagUrl = getCurrencyFlagUtil(currentUser?.currency);

  // Toggle day expansion
  const toggleDayExpansion = (dayId: number) => {
    setLearningDays((prevDays) =>
      prevDays.map((day) =>
        day.id === dayId ? { ...day, isExpanded: !day.isExpanded } : day
      )
    );
  };

  // Handle lesson selection
  const handleLessonSelect = (lesson: string) => {
    if (setActiveLesson) {
      setActiveLesson(lesson);
    }
  };

  // Handle sidebar toggle
  const handleToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Handle reset progress
  const handleReset = () => {
    // Reset learning progress logic here
    setIsResetOpen(false);
  };

  return (
    <div
      className={`fixed top-0 z-40 h-full w-full max-w-[356px] shadow-md p-6 overflow-y-auto transition-transform duration-300 ease-in-out transform bg-white ${
        isSidebarOpen
          ? "lg:translate-x-0"
          : "lg:translate-x-0 -translate-x-full"
      }`}
    >
      <div className="h-full flex flex-col">
        {/* Logo */}
        <div>
          <img
            src="/assets/images/logo/logo.png"
            alt="AME Logo"
            className="h-11 w-auto mb-4"
          />
          <button
            onClick={handleToggle}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 lg:hidden"
          >
            <FiX size={20} />
          </button>
        </div>

        <Divider className="bg-gray-200" />

        {/* Key Learnings Title */}
        <div className="py-4">
          <h2 className="text-xl font-bold text-gray-700">Key Learnings</h2>
        </div>

        <Divider className="bg-gray-200" />

        {/* Search Box */}
        <div className="py-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search"
              className="w-full px-4 py-2 border border-gray-200 rounded-md pl-10"
            />
            <div className="absolute left-3 top-3">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Learning Days */}
        <div className="flex-1 overflow-y-auto mt-2">
          {learningDays.map((day) => (
            <div key={day.id} className="mb-2">
              <div
                className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50 rounded"
                onClick={() => !day.isLocked && toggleDayExpansion(day.id)}
              >
                <div className="flex items-center">
                  {day.isLocked ? (
                    <FiLock size={16} className="text-gray-400 mr-2" />
                  ) : (
                    <div className="w-4 mr-2"></div>
                  )}
                  <span
                    className={`font-medium ${
                      day.isLocked ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    {day.title}
                  </span>
                </div>
                {!day.isLocked && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={`transform ${
                      day.isExpanded ? "rotate-180" : "rotate-0"
                    } transition-transform`}
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>

              {/* Activities for this day */}
              {day.isExpanded && (
                <div className="ml-6 space-y-1 mt-1">
                  {day.activities.map((activity) => (
                    <div
                      key={activity.id}
                      className={`flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50 rounded ${
                        activity.isActive ? "bg-blue-50" : ""
                      }`}
                      onClick={() =>
                        !activity.isLocked && handleLessonSelect(activity.title)
                      }
                    >
                      <div className="flex items-center">
                        {activity.isLocked ? (
                          <FiLock size={14} className="text-gray-400 mr-2" />
                        ) : (
                          <div className="w-4 mr-2"></div>
                        )}
                        <span
                          className={`text-sm ${
                            activity.isLocked
                              ? "text-gray-400"
                              : activity.isActive
                              ? "text-blue-600 font-medium"
                              : "text-gray-700"
                          }`}
                        >
                          {activity.title}
                        </span>
                      </div>
                      {activity.isActive && (
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom section with Reset and User Profile */}
        <div className="mt-6">
          {/* Reset Button */}
          <Button
            className="flex items-center gap-3 focus:outline-none w-full bg-transparent rounded-lg border-gray-300 font-bold text-sm !text-gray-700"
            onPress={() => setIsResetOpen(true)}
          >
            <FiRefreshCcw size={20} />
            Reset Lesson
          </Button>

          <Divider className="bg-gray-200 h-0.5 my-4" />

          {/* User Profile */}
          <div className="flex gap-10 justify-between items-center pl-2 mt-4 w-full">
            <div className="flex gap-3 items-center self-stretch my-auto font-bold text-gray-900">
              <img
                src={currencyFlagUrl}
                alt="Currency flag"
                className="h-10 w-10 rounded-full"
              />
              <span className="self-stretch my-auto text-gray-900 text-nowrap">
                {currentUser?.name || "Justin Green"}
              </span>
            </div>

            <Button
              className="w-auto !p-0 flex focus:outline-none items-center gap-3 bg-transparent hover:border-transparent border-transparent font-bold text-sm !text-gray-700"
              onPress={() => setIsLogoutOpen(true)}
            >
              Logout
              <FiLogOut size={20} className="font-bold" />
            </Button>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmationModal isOpen={isLogoutOpen} onOpenChange={setIsLogoutOpen}>
        <div className="text-lg font-semibold mb-3">
          Are you Sure want to Logout?
        </div>
        <div className="flex justify-end gap-2">
          <Button onPress={() => setIsLogoutOpen(false)}>No</Button>
          <Button
            variant="solid"
            className="bg-gray-700 text-white"
            onPress={() => logout()}
          >
            Yes
          </Button>
        </div>
      </ConfirmationModal>

      {/* Reset Confirmation Modal */}
      <ConfirmationModal isOpen={isResetOpen} onOpenChange={setIsResetOpen}>
        <div className="text-lg font-semibold mb-3">
          Are you sure you want to reset your progress?
        </div>
        <div className="text-sm text-gray-500 mb-4">
          This will clear all your progress in this lesson.
        </div>
        <div className="flex justify-end gap-2">
          <Button onPress={() => setIsResetOpen(false)}>Cancel</Button>
          <Button
            variant="solid"
            className="bg-red-600 text-white"
            onPress={handleReset}
          >
            Reset
          </Button>
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default LearningAreaSidebar;
